import type { BatchDetailResponse } from "../../gen/models";
import { SimulatorDrawer } from "../SimulatorDrawer";
import {
  BatchPromoteModal,
  BatchRejectModal,
  BatchArchiveModal,
  BatchReopenModal,
} from "./BatchReviewModals";

interface BatchDetailModalsProps {
  batch: BatchDetailResponse | null;
  excludedRuleIds: Set<string>;
  excludedKnowledgeIds: Set<string>;
  excludedGuardrailIds: Set<string>;
  actionLoading: boolean;
  isPromoteModalOpen: boolean;
  onClosePromoteModal: () => void;
  onConfirmPromote: () => void;
  isRejectModalOpen: boolean;
  onCloseRejectModal: () => void;
  onConfirmReject: () => void;
  isArchiveModalOpen: boolean;
  onCloseArchiveModal: () => void;
  onConfirmArchive: () => void;
  isReopenModalOpen: boolean;
  onCloseReopenModal: () => void;
  onConfirmReopen: () => void;
  isSimulatorOpen: boolean;
  onCloseSimulator: () => void;
}

export function BatchDetailModals({
  batch,
  excludedRuleIds,
  excludedKnowledgeIds,
  excludedGuardrailIds,
  actionLoading,
  isPromoteModalOpen,
  onClosePromoteModal,
  onConfirmPromote,
  isRejectModalOpen,
  onCloseRejectModal,
  onConfirmReject,
  isArchiveModalOpen,
  onCloseArchiveModal,
  onConfirmArchive,
  isReopenModalOpen,
  onCloseReopenModal,
  onConfirmReopen,
  isSimulatorOpen,
  onCloseSimulator,
}: BatchDetailModalsProps) {
  const draftRules = batch?.draft_rules || [];
  const rulesToActivateCount = draftRules.length - excludedRuleIds.size;
  const rulesToSupersedeCount = draftRules.filter(
    (r: any) => r.supersedes_rule_id && !excludedRuleIds.has(r.id)
  ).length;
  const knowledgeToPublishCount =
    (batch?.draft_knowledge_items?.length || 0) - excludedKnowledgeIds.size;
  const guardrailsToActivateCount =
    (batch?.draft_guardrails?.length || 0) - excludedGuardrailIds.size;

  return (
    <>
      {/* Promotion Confirmation Modal */}
      <BatchPromoteModal
        isOpen={isPromoteModalOpen}
        onClose={onClosePromoteModal}
        onConfirm={onConfirmPromote}
        actionLoading={actionLoading}
        rulesToActivateCount={rulesToActivateCount}
        rulesToSupersedeCount={rulesToSupersedeCount}
        knowledgeToPublishCount={knowledgeToPublishCount}
        guardrailsToActivateCount={guardrailsToActivateCount}
      />

      {/* Rejection Confirmation Modal */}
      <BatchRejectModal
        isOpen={isRejectModalOpen}
        onClose={onCloseRejectModal}
        onConfirm={onConfirmReject}
        actionLoading={actionLoading}
      />

      {/* Archive Confirmation Modal */}
      <BatchArchiveModal
        isOpen={isArchiveModalOpen}
        onClose={onCloseArchiveModal}
        onConfirm={onConfirmArchive}
        actionLoading={actionLoading}
        batchTitle={batch?.title}
      />

      {/* Reopen / Reset to Collecting Confirmation Modal */}
      <BatchReopenModal
        isOpen={isReopenModalOpen}
        onClose={onCloseReopenModal}
        onConfirm={onConfirmReopen}
        actionLoading={actionLoading}
        batchTitle={batch?.title}
        draftRulesCount={batch?.draft_rules?.length || 0}
        draftKnowledgeCount={batch?.draft_knowledge_items?.length || 0}
        draftGuardrailsCount={batch?.draft_guardrails?.length || 0}
      />

      {/* Sandbox Simulator Drawer */}
      {batch && (
        <SimulatorDrawer
          isOpen={isSimulatorOpen}
          onClose={onCloseSimulator}
          batchId={batch.id}
          batchTitle={batch.title}
        />
      )}
    </>
  );
}
